// PdfHighlightModal — inline PDF preview with text-search highlight overlay.
//
// Loads pdfjs from CDN on demand, fetches highlight coordinates from
// the backend pymupdf search endpoint, and overlays a yellow box on
// the matching page. Used by RepoView and AIImportView when the user
// clicks "View in document" on an AI-extracted field.

import { useState, useEffect, useRef } from "react";
import { T } from "../theme";
import { apiCall } from "../apiClient";
import { Modal } from "./primitives";

export const PdfHighlightModal = ({ file = null, searchText = '', sectionRef = '', base64 = '', fieldId = undefined, fieldName = undefined, fieldValue = undefined, onClose = undefined }: { file?: any; searchText?: string; sectionRef?: string; base64?: string; fieldId?: string; fieldName?: string; fieldValue?: string; onClose?: () => void }) => {
  const canvasContainerRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    if (!file || (!searchText && !fieldValue)) return;
    let cancelled = false;

    const loadPdfJs = async () => {
      if (!window.pdfjsLib) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        await new Promise((resolve, reject) => { script.onload = resolve; script.onerror = reject; document.head.appendChild(script); });
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      }
    };

    const renderAllPagesWithHighlight = async (pdf: any, highlightPage: any, highlights: any) => {
      const container = canvasContainerRef.current;
      if (!container || cancelled) return;
      container.innerHTML = '';
      const scale = 1.5;

      // Create placeholder divs for all pages first so the container is scrollable immediately
      const pageDivs: any[] = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const pageDiv = document.createElement('div');
        pageDiv.style.cssText = 'position:relative;margin:0 auto 12px auto;background:#fff;border-radius:4px;box-shadow:0 2px 8px rgba(0,0,0,0.12);min-height:200px';
        pageDiv.dataset.pageNum = String(i);
        // Page number label
        const label = document.createElement('div');
        label.style.cssText = 'position:absolute;top:4px;right:8px;font-size:10px;color:#999;z-index:1';
        label.textContent = `Page ${i}`;
        pageDiv.appendChild(label);
        container.appendChild(pageDiv);
        pageDivs.push(pageDiv);
      }

      // Render pages, prioritizing the highlight page
      const renderOnePage = async (pageNum: number) => {
        if (cancelled) return;
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale });
        const pageDiv = pageDivs[pageNum - 1];
        pageDiv.style.width = viewport.width + 'px';
        pageDiv.style.height = viewport.height + 'px';
        pageDiv.style.minHeight = '';

        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.display = 'block';
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        // Insert canvas before the label
        pageDiv.insertBefore(canvas, pageDiv.firstChild);

        if (pageNum === highlightPage && highlights) {
          for (let hi = 0; hi < highlights.length; hi++) {
            const hl = highlights[hi];
            const hlDiv = document.createElement('div');
            hlDiv.setAttribute('data-highlight', 'true');
            hlDiv.style.cssText = `position:absolute;left:${hl.x0 * scale}px;top:${hl.y0 * scale}px;width:${(hl.x1 - hl.x0) * scale}px;height:${(hl.y1 - hl.y0) * scale}px;background:rgba(255,200,0,0.35);border:1px solid rgba(255,170,0,0.5);border-radius:2px;pointer-events:none;mix-blend-mode:multiply`;
            pageDiv.appendChild(hlDiv);
          }
        }
      };

      // Render highlight page first, then scroll to the first highlight, then render remaining
      if (highlightPage) {
        await renderOnePage(highlightPage);
        const target = pageDivs[highlightPage - 1];
        if (target && container) {
          setTimeout(() => {
            // Find the first highlight overlay within this page div
            const hlEl = target.querySelector('[data-highlight]');
            if (hlEl) {
              // Scroll so the highlight is roughly 1/3 from the top of the viewport
              const hlTop = target.offsetTop + hlEl.offsetTop;
              const viewportThird = container.clientHeight / 3;
              container.scrollTop = hlTop - container.offsetTop - viewportThird;
            } else {
              container.scrollTop = target.offsetTop - container.offsetTop;
            }
          }, 80);
        }
      }

      // Render remaining pages
      for (let i = 1; i <= pdf.numPages; i++) {
        if (i === highlightPage || cancelled) continue;
        await renderOnePage(i);
      }
    };

    const doSearch = async () => {
      await loadPdfJs();
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      if (cancelled) return;

      // Call backend pymupdf search
      let highlightPage = null, highlights = null;
      try {
        const payload: Record<string, any> = { search_text: searchText, section_ref: sectionRef || null };
        if (base64) payload.document_base64 = base64;
        if (fieldId) payload.field_id = fieldId;
        if (fieldName) payload.field_name = fieldName;
        if (fieldValue) payload.field_value = fieldValue;
        const data = await apiCall('/extraction/highlight', { method: 'POST', body: JSON.stringify(payload) });
        if (data?.success && data.result) {
          if (cancelled) return;
          setResult(data.result);
          highlightPage = data.result.page;
          highlights = data.result.highlights;
        } else {
          setError(data?.error || 'Text not found');
        }
      } catch (e: any) {
        setError('Server search failed');
      }

      // Render all pages, with highlights on the found page
      if (!cancelled) {
        await renderAllPagesWithHighlight(pdf, highlightPage, highlights);
        setLoading(false);
      }
    };

    doSearch();
    return () => { cancelled = true; };
  }, [file, searchText, sectionRef, base64]);

  return (
    <Modal open={true} onClose={onClose} title="Source Document" w={960}>
      <div style={{ fontSize: 11, color: T.txM, marginBottom: 8 }}>
        {searchText && <span>Highlighting: <strong style={{ color: T.acc }}>"{searchText.substring(0, 100)}{searchText.length > 100 ? "..." : ""}"</strong></span>}
        {result && <span style={{ marginLeft: 8, color: T.grn }}>Found on page {result.page} of {result.total_pages}</span>}
        {result && result.ai_anchor_used && <span style={{ marginLeft: 8, color: T.txD, fontStyle: 'italic' }}>(AI-located)</span>}
        {result && result.low_confidence && <span style={{ marginLeft: 8, color: T.ylw, fontStyle: 'italic' }}>(approximate match — may be the wrong section)</span>}
        {error && <span style={{ marginLeft: 8, color: T.ylw }}>{error}</span>}
      </div>
      {!file && <div style={{ textAlign: "center", padding: 20, color: T.red }}>PDF file not available. The file may have been removed from the upload list.</div>}
      {file && loading && <div style={{ textAlign: "center", padding: 20, color: T.txM }}>Searching document...</div>}
      <div ref={canvasContainerRef} style={{ maxHeight: "75vh", overflow: "auto", backgroundColor: "#e8e8e8", borderRadius: 6, padding: 16, textAlign: "center" }} />
    </Modal>
  );
};
